import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { ComponentProps, ForwardedRef, forwardRef } from 'react'
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Transitions } from '~/assets/style/tokens'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { WithClassName } from '~/types/utils'

const statesConfig = {
  true: {
    left: {
      width: 2.17571,
      height: 6.11829,
      transform: 'rotate(135 5.46143 10.56143) translate(5.46143 10.56143)',
    },
    right: {
      width: 2.17571,
      height: 11.9664,
      transform: 'rotate(45 10.56143 2.5) translate(10.56143 2)',
    },
  },
  false: {
    left: {
      width: 2.17571,
      height: 11.9664,
      transform: 'rotate(135 11 9.46143) translate(11 9.46143)',
    },
    right: {
      width: 2.17571,
      height: 11.9664,
      transform: 'rotate(45 9.46143 1) translate(9.46143 1)',
    },
  },
}

/**
 * `solid` is the switch as the app has always drawn it: green for on, grey for off.
 *
 * `outline` is the quieter one, for a form that is mostly toggles. A bidder rule is a page-long list of
 * them, and a column of saturated green reads as a status report on something already happening rather
 * than as a set of choices being made — so the on state is the blue the rest of the form is built in, and
 * the off state is an outline instead of a filled grey.
 */
type ToggleAppearance = 'solid' | 'outline'

type ToggleProps = WithClassName & Omit<ComponentProps<'input'>, 'value'> & { value?: boolean; trackingLabel: TrackingLabel; variant?: 'small' | 'regular'; appearance?: ToggleAppearance }

const ToggleIcon = ({ value, variant }: Pick<ToggleProps, 'value' | 'variant'>) => {
  const config = statesConfig[`${!!value || false}`]
  const iconSize = variant === 'small' ? 8 : 10
  return (
    <svg width={`${iconSize}`} height={`${iconSize}`} viewBox={'0 0 12 12'} fill={'currentColor'} xmlns={'http://www.w3.org/2000/svg'}>
      <rect x={'0'} y={'0'} width={config.left.width} height={config.left.height} rx={'1.08786'} transform={config.left.transform} />
      <rect x={'0'} y={'0'} width={config.right.width} height={config.right.height} rx={'1.08786'} transform={config.right.transform} />
    </svg>
  )
}

const Circle = styled.span<Pick<ToggleProps, 'variant' | 'appearance' | 'value'>>`
  ${({ variant }) =>
    variant === 'small'
      ? css`
          width: 12px;
          height: 12px;
        `
      : css`
          width: 16px;
          height: 16px;
        `}

  border-radius: 100%;
  /* On an outline toggle that is off there is no filled track behind the knob, so a white one would be
     invisible: it takes the track's own colour instead, and the mark inside it goes white. */
  ${({ appearance, value }) =>
    appearance === 'outline' && !value
      ? css`
          background-color: currentColor;
          color: ${Colors.White};
        `
      : css`
          background-color: ${Colors.White};
        `}
  display: inline-flex;
  justify-content: center;
  align-items: center;
  transition: ${Transitions.quick};
`

const ToggleWrapper = styled.span<Omit<ToggleProps, 'trackingLabel'>>`
  border-radius: 24px;
  display: inline-flex;
  box-sizing: border-box;
  color: ${Colors.SlateGrey};
  cursor: pointer;
  transition: ${Transitions.quick};
  user-select: none;
  border: 1px solid transparent;

  ${({ variant }) =>
    variant === 'small'
      ? css`
          width: 32px;
          height: 18px;
          padding: 3px;
        `
      : css`
          width: 44px;
          height: 24px;
          padding: 4px;
        `}

  ${({ value, variant, appearance }) =>
    value
      ? css`
          padding-left: ${variant === 'small' ? '16px' : '24px'};
          background-color: ${appearance === 'outline' ? Colors.King : Colors.Success};
          color: ${appearance === 'outline' ? Colors.King : Colors.Success};
        `
      : appearance === 'outline'
        ? css`
            /* Off is a hollow track with a filled knob, so the two states differ in weight as well as
               in colour — a row of them reads as "these are off" rather than as "these are grey". */
            padding-left: 4px;
            background-color: transparent;
            border-color: ${Colors.Silver};
            color: ${Colors.Silver};
          `
        : css`
            padding-left: 4px;
            background-color: ${Colors.Hurricane};
            color: ${Colors.Hurricane};
          `};

  &:hover {
    ${({ value, appearance }) =>
      value
        ? css`
            background-color: ${appearance === 'outline' ? Colors.Cobalt : Colors.Forest};
            color: ${appearance === 'outline' ? Colors.Cobalt : Colors.Forest};
          `
        : appearance === 'outline'
          ? css`
              border-color: ${Colors.SlateGrey};
              color: ${Colors.SlateGrey};
            `
          : css`
              background-color: ${Colors.SlateGrey};
              color: ${Colors.SlateGrey};
            `};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${Colors.Silver};
      color: ${Colors.Silver};
      cursor: not-allowed;

      :hover {
        background-color: ${Colors.Silver};
        color: ${Colors.Silver};
      }
    `}

  & > input {
    display: none;
    position: absolute;
  }
`

const BaseToggle = (
  { value, disabled, onClick, className, trackingLabel, variant = 'regular', appearance = 'solid', 'aria-label': ariaLabel, checked: _checked, ...props }: ToggleProps,
  ref: ForwardedRef<HTMLInputElement>
) => {
  return (
    /* The real input is `display: none`, which takes it out of the accessibility tree and out of the
       tab order — so the wrapper has to carry the switch itself, or the control is mouse-only and
       screen readers never see it. `aria-label` rides here rather than on the hidden input for the
       same reason. Keyboard activation re-dispatches a click so there is one code path, and the
       disabled case stays inert through the same guard the mouse takes. */
    <ToggleWrapper
      variant={variant}
      appearance={appearance}
      disabled={disabled}
      className={className}
      onClick={disabled ? () => null : onClick}
      value={value}
      data-tracking={trackingLabel}
      role={'switch'}
      aria-checked={!!value}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault()
          event.currentTarget.click()
        }
      }}
    >
      <Circle variant={variant} appearance={appearance} value={value}>
        <ToggleIcon value={value} variant={variant} />
      </Circle>
      <input {...props} disabled={disabled} type={'checkbox'} checked={!!value} onChange={() => {}} ref={ref} />
    </ToggleWrapper>
  )
}
export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(BaseToggle)

type ToggleInputProps<T extends FieldValues> = Omit<ToggleProps, 'value' | 'ref' | 'onChange'> & Omit<UseControllerProps<T>, 'defaultValue'> & { onChange?: (value: boolean) => void }
export const ToggleInput = <T extends FieldValues>({ name, trackingLabel, control, rules, shouldUnregister, onChange, onClick, className, ...props }: ToggleInputProps<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <Toggle
          trackingLabel={trackingLabel}
          {...props}
          value={field.value}
          onClick={() => {
            field.onChange(!field.value)
            onChange && onChange(!field.value)
          }}
        />
      )}
    />
  )
}
